import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import axios from 'axios';
import useTranslation from '../hooks/useTranslation';
import { FieldGroup, InputGroup, Row } from '../components/Input';
import Button from '../components/Button';
import Checkbox from '../components/Checkbox';
import SelectGames from '../components/SelectGames';
import TagsSelect from '../components/TagsSelect';
import EventCostEstimator from '../components/EventCostEstimator';
import WalletWidget from '../components/WalletWidget';
import StreamerAssignmentManager from '../components/StreamerAssignmentManager';
import { eventService } from '../services/eventService';
import { ENV_CONFIG } from '../config/environment-unified';

const ThemedSelect = styled.select`
  position: relative;
  width: 100%;
  padding: 10px 15px;
  border-radius: 5px;
  background-color: ${({ theme }) => theme.color.secondaryBg};
  color: ${({ theme }) => theme.color.darkerText};
  font-size: ${({ theme }) => theme.fontSize.xs};
  line-height: 1.5;
  transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out,
    border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

  &:focus {
    box-shadow: 0 0 0 0.2rem rgba(69, 105, 255, 0.8);
    background-color: ${({ theme }) => theme.color.secondaryBg};
    outline: 0;
  }
`;

const CreateEvent = () => {
  const history = useHistory();
  const isLoggedIn = useSelector(state => state.user.isLoggedIn);
  const user = useSelector(state => state.user.data);
  const { t } = useTranslation();

  const getBrowserTimeZone = () => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
    } catch (_) {
      return null;
    }
  };

  const openLoginModal = () => {
    window.dispatchEvent(new CustomEvent("corearena:auth:open", { detail: { tabIndex: 1 } }));
  };

  // Flow State
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [createdEventSlug, setCreatedEventSlug] = useState(null);
  const basicNameRef = useRef(null);
  const basicDescriptionRef = useRef(null);
  const basicStartDateRef = useRef(null);
  const basicEndDateRef = useRef(null);
  const durationHoursRef = useRef(null);
  const teamSizeRef = useRef(null);
  const prizePoolRef = useRef(null);
  const maxParticipantsRef = useRef(null);
  const ticketPriceRef = useRef(null);

  // Data State
  const [categories, setCategories] = useState([]);
  const [basicInfo, setBasicInfo] = useState({
    name: '',
    description: '',
    startDate: '',
    endDate: '',
    durationHours: 1
  });
  const [eventData, setEventData] = useState({
    // Basic info is handled separately in `basicInfo` (Step 2)
    name: '',
    description: '',
    startDate: '',
    endDate: '',
    categoryId: '',
    gameId: '', // Selected game
    gameName: '', // Game name for display
    tags: [], // Array of tag objects {label, value}
    options: {
      restream: false,
      autoReplay: true,
      multiPov: false,
      autoHighlights: false,
      chatMode: 'public', // public, subscribers, disabled
      teamSize: 0,
      prizePool: 0,
      maxParticipants: 0,
      tournamentFormat: 'single-elimination', // single-elimination, double-elimination, round-robin, swiss
      sponsors: false
    },
    ticketConfig: {
      type: 'single', // single, full
      price: 0
    },
    isPublic: true
  });
  const [invitedStreamers, setInvitedStreamers] = useState([]);
  const [creationFeeState, setCreationFeeState] = useState({ amount: 0, canAfford: true });

  // Fetch Categories and Games on Mount
  useEffect(() => {
    if (!isLoggedIn) {
      // Do not navigate away; open the existing auth popup instead.
      openLoginModal();
      return;
    }
    const loadData = async () => {
      try {
        // Load categories
        const cats = await eventService.getCategories();
        setCategories(cats.categories || []);

      } catch (err) {
        console.error("Failed to load data", err);
      }
    };
    loadData();
  }, [isLoggedIn, history]);

  // Prefill tags from the selected game's default tags (if any).
  useEffect(() => {
    const gameId = eventData.gameId;
    if (!gameId || gameId === 'random') return;

    let cancelled = false;

    const loadGameTags = async () => {
      try {
        const res = await axios.get(`${ENV_CONFIG.GAME_ENDPOINT}${gameId}`, { withCredentials: true });
        const rawTags = Array.isArray(res.data?.tags) ? res.data.tags : [];
        const uniqueTags = [...new Set(rawTags.filter(Boolean).map(t => String(t).trim()).filter(Boolean))];

        if (cancelled || uniqueTags.length === 0) return;

        setEventData(prev => {
          if (prev.gameId !== gameId) return prev;
          if (Array.isArray(prev.tags) && prev.tags.length > 0) return prev;
          return {
            ...prev,
            tags: uniqueTags.map(tag => ({ label: tag, value: tag }))
          };
        });
      } catch (err) {
        console.warn('⚠️ Failed to prefill game tags:', err?.message || err);
      }
    };

    loadGameTags();
    return () => {
      cancelled = true;
    };
  }, [eventData.gameId]);

  // --- Handlers ---

  // UX: when a numeric field is initialized to 0, focusing it should allow immediate replace/delete.
  // On macOS, users often only have Backspace (no forward-delete), so if the caret lands before "0",
  // Backspace won't remove it. Selecting the value on focus makes behavior feel standard.
  const selectAllIfZero = (e) => {
    const v = e?.target?.value;
    if (v === '0') {
      // Run after click/mouseup so selection isn't immediately lost.
      setTimeout(() => {
        try {
          e.target.select();
        } catch (_) {
          // no-op
        }
      }, 0);
    }
  };

  // Robust generic handler for all form inputs including nested options
  // Robust generic handler for all form inputs including nested options
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    console.log("CreateEvent: handleChange triggered", { name, value, type, checked });

    // Determine the actual value to store
    // For checkboxes, use `checked`
    // For number inputs, convert string to Number (unless empty)
    const val = type === 'checkbox'
      ? checked
      : (type === 'number'
        ? (value === '' ? '' : Number(value))
        : value);

    if (name.startsWith('options.')) {
      const optionName = name.split('.')[1];
      console.log(`CreateEvent: Updating options.${optionName} to`, val);
      setEventData(prev => ({
        ...prev,
        options: {
          ...prev.options,
          [optionName]: val
        }
      }));
    } else if (name.startsWith('ticket.')) {
      const ticketField = name.split('.')[1];
      console.log(`CreateEvent: Updating ticketConfig.${ticketField} to`, val);
      setEventData(prev => ({
        ...prev,
        ticketConfig: {
          ...prev.ticketConfig,
          [ticketField]: val
        }
      }));
    } else {
      console.log(`CreateEvent: Updating ${name} to`, val);
      setEventData(prev => ({
        ...prev,
        [name]: val
      }));
    }
  };
  const computeEndDateFromStartAndHours = ({ startDate, durationHours, maxDurationMinutes }) => {
    const s = new Date(startDate);
    if (Number.isNaN(s.getTime())) return null;

    const h = Number(durationHours);
    const requestedMinutes = Number.isFinite(h) ? Math.max(0, h * 60) : 0;
    const maxM = (maxDurationMinutes !== null && maxDurationMinutes !== undefined)
      ? Number(maxDurationMinutes)
      : null;
    const effectiveMinutes = (maxM !== null && Number.isFinite(maxM) && maxM >= 0)
      ? Math.min(requestedMinutes, maxM)
      : requestedMinutes;

    const e = new Date(s.getTime() + effectiveMinutes * 60 * 1000);
    const yyyy = e.getFullYear();
    const mm = String(e.getMonth() + 1).padStart(2, '0');
    const dd = String(e.getDate()).padStart(2, '0');
    const hh = String(e.getHours()).padStart(2, '0');
    const min = String(e.getMinutes()).padStart(2, '0');
    return {
      endDate: `${yyyy}-${mm}-${dd}T${hh}:${min}`,
      durationHours: effectiveMinutes / 60,
      wasCapped: maxM !== null && Number.isFinite(maxM) && effectiveMinutes < requestedMinutes
    };
  };

  // Step 2 UX: keep endDate computed automatically.
  useEffect(() => {
    if (step !== 2) return;
    const selectedCategory = categories.find(c => String(c.id) === String(eventData.categoryId));
    const maxMinutes = selectedCategory?.maxDurationMinutes ?? null;
    const startDate = basicStartDateRef.current?.value ?? basicInfo.startDate;
    const hours = durationHoursRef.current?.value ?? basicInfo.durationHours;
    if (!startDate) return;

    const computed = computeEndDateFromStartAndHours({ startDate, durationHours: hours, maxDurationMinutes: maxMinutes });
    if (!computed) return;

    if (basicEndDateRef.current) basicEndDateRef.current.value = computed.endDate;
    if (durationHoursRef.current) durationHoursRef.current.value = String(computed.durationHours);
    setBasicInfo(prev => ({ ...prev, startDate, endDate: computed.endDate, durationHours: computed.durationHours }));
  }, [step, eventData.categoryId, categories, basicInfo.startDate, basicInfo.durationHours]);

  // Step 1: Choose Category (before creating draft)
  const handleStep1Submit = async (e) => {
    e.preventDefault();
    if (!eventData.categoryId) {
      toast.error(t('createevent.please_select_an', 'Please select an event category'));
      return;
    }
    setStep(2);
  };

  // Step 2: Create Draft, then set Category immediately
  const handleStep2Submit = async () => {
    setLoading(true);
    try {
      const name = (basicNameRef.current?.value ?? basicInfo.name ?? '').toString();
      const description = (basicDescriptionRef.current?.value ?? basicInfo.description ?? '').toString();
      const startDate = (basicStartDateRef.current?.value ?? basicInfo.startDate ?? '').toString();
      const durationHours = (durationHoursRef.current?.value ?? basicInfo.durationHours ?? 0).toString();

      if (!name || !description || !startDate) {
        toast.error(t('createevent.fill_required_fields', 'Please fill: Event Name, Description, Start date'));
        return;
      }

      const selectedCategory = categories.find(c => String(c.id) === String(eventData.categoryId));
      const maxMinutes = selectedCategory?.maxDurationMinutes ?? null;
      const computed = computeEndDateFromStartAndHours({ startDate, durationHours, maxDurationMinutes: maxMinutes });
      if (!computed) {
        toast.error(t('createevent.invalid_start_date', 'Invalid start date'));
        return;
      }
      const endDate = computed.endDate;
      if (basicEndDateRef.current) basicEndDateRef.current.value = endDate;
      if (durationHoursRef.current) durationHoursRef.current.value = String(computed.durationHours);

      // IMPORTANT: <input type="datetime-local" /> yields a string without timezone.
      // Convert to an ISO timestamp in the browser (creator's local timezone) before sending to backend,
      // so the backend doesn't accidentally interpret it using the server's timezone.
      const startDateObj = new Date(startDate);
      if (Number.isNaN(startDateObj.getTime())) {
        toast.error(t('createevent.invalid_start_date', 'Invalid start date'));
        return;
      }
      const endDateObj = endDate ? new Date(endDate) : null;
      if (endDate && (!endDateObj || Number.isNaN(endDateObj.getTime()))) {
        toast.error(t('createevent.invalid_end_date', 'Invalid end date'));
        return;
      }
      const timezone = getBrowserTimeZone();

      const draft = await eventService.createDraft({
        name,
        description,
        startDate: startDateObj.toISOString(),
        endDate: endDateObj ? endDateObj.toISOString() : null,
        timezone: timezone || undefined
      });

      const draftSlug = draft?.slug;
      if (!draftSlug) {
        toast.error("Failed to create event draft");
        return;
      }

      setCreatedEventSlug(draftSlug);
      // Keep a copy in eventData for any downstream UI that expects it
      setEventData(prev => ({
        ...prev,
        name,
        description,
        startDate,
        endDate: endDate || ''
      }));
      setBasicInfo({ name, description, startDate, endDate, durationHours: computed.durationHours });

      await eventService.setupCategory(draftSlug, eventData.categoryId);
      setStep(3);
      toast.success("Draft created! Category selected. Configure your event.");
    } catch (err) {
      toast.error("Failed to create event draft");
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Config
  const handleStep3Submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Convert tags from objects to array of strings
      const tagsArray = eventData.tags.map(tag => tag.value || tag.label || tag);

      // Normalize numeric fields (allow empty string while typing in UI)
      const refNumber = (ref, fallback = 0) => {
        const raw = ref?.current?.value;
        if (raw === undefined || raw === null) return fallback;
        if (raw === '') return 0;
        const n = Number(raw);
        return Number.isFinite(n) ? n : fallback;
      };
      const normalizedOptions = {
        ...eventData.options,
        // Tournament fields
        teamSize: refNumber(teamSizeRef, eventData.options.teamSize === '' ? 0 : Number(eventData.options.teamSize || 0)),
        prizePool: refNumber(prizePoolRef, eventData.options.prizePool === '' ? 0 : Number(eventData.options.prizePool || 0)),
        // Workshop fields
        maxParticipants: refNumber(maxParticipantsRef, eventData.options.maxParticipants === '' ? 0 : Number(eventData.options.maxParticipants || 0))
      };
      // Max Players is configured later in the Bracket tab
      delete normalizedOptions.maxPlayers;

      const normalizedTicketConfig = {
        ...eventData.ticketConfig,
        price: refNumber(ticketPriceRef, eventData.ticketConfig.price === '' ? 0 : Number(eventData.ticketConfig.price || 0))
      };

      await eventService.configureEvent(createdEventSlug, {
        options: normalizedOptions,
        ticketConfig: normalizedTicketConfig,
        isPublic: eventData.isPublic,
        gameId: eventData.gameId || null,
        tags: tagsArray
      });

      // Check category to determine next step
      const selectedCategory = categories.find(cat => cat.id === eventData.categoryId);
      const categorySlug = selectedCategory?.slug;

      if (categorySlug === 'stream' || categorySlug === 'workshop') {
        // Stream and Workshop: auto-publish (otherwise they don't appear in /events lists)
        try {
          await eventService.publishEvent(createdEventSlug);
          toast.success("Event published! Redirecting to Go Live...");
          // Pass the event slug as a URL parameter so StreamerDashboard can link the stream
          history.push(`/stream-dashboard?eventSlug=${createdEventSlug}`);
        } catch (err) {
          console.error('❌ Failed to auto-publish stream/workshop event:', err);
          if (err?.response?.status === 402) {
            toast.error(err?.response?.data?.message || "Insufficient balance to publish event");
          } else {
            toast.error("Failed to publish event");
          }
          return;
        }
      } else if (categorySlug === 'tournament') {
        // Tournament: Proceed to participant management
        setStep(4);
        toast.success("Configuration saved! Now manage participants.");
      } else {
        // Fallback
        setStep(4);
        toast.success("Configuration saved!");
      }
    } catch (err) {
      toast.error("Failed to save configuration");
    } finally {
      setLoading(false);
    }
  };

  // Step 4: Finish / Publish
  const handleFinish = async () => {
    setLoading(true);
    try {
      if (!creationFeeState.canAfford) {
        toast.error("Insufficient balance to publish this event");
        return;
      }
      await eventService.publishEvent(createdEventSlug);
      toast.success("Event published successfully!");
      // Redirect to Event Dashboard for organizers to manage their event
      history.push(`/events/${createdEventSlug}/dashboard`);
    } catch (err) {
      if (err?.response?.status === 402) {
        toast.error(err?.response?.data?.message || "Insufficient balance to publish event");
      } else {
        toast.error("Failed to publish event");
      }
    } finally {
      setLoading(false);
    }
  };

  // --- Renders ---

  const renderStep1 = () => {
    return (
      <form onSubmit={handleStep1Submit}>
        <h4>{t('createevent.step1_choose_category', 'Step 1: Choose Category')}</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
          {categories.map(cat => (
            (() => {
              const maxMinutes = cat?.maxDurationMinutes ?? null;
              const maxHours = (maxMinutes === null || maxMinutes === undefined) ? null : (Number(maxMinutes) / 60);
              const maxDurationLabel = (maxHours === null || !Number.isFinite(maxHours))
                ? t('createevent.unlimited', 'Unlimited')
                : `${maxHours} ${t('createevent.hours', 'hours')}`;

              return (
                <CategoryCard
                  key={cat.id}
                  selected={eventData.categoryId === cat.id}
                  onClick={() => setEventData(prev => ({ ...prev, categoryId: cat.id }))}
                  title={[
                    typeof cat.creationGcoinPrice === 'number'
                      ? `${t('createevent.creation_fee', 'Creation fee')}: ${cat.creationGcoinPrice} G-Coins`
                      : null,
                    (maxHours === null || !Number.isFinite(maxHours))
                      ? `${t('createevent.max_duration', 'Max duration')}: ${t('createevent.unlimited', 'Unlimited')}`
                      : `${t('createevent.max_duration', 'Max duration')}: ${maxHours} ${t('createevent.hours', 'hours')}`
                  ].filter(Boolean).join(' • ')}
                >
                  <div style={{ fontSize: '2rem' }}>{cat.icon}</div>
                  <strong>{cat.name}</strong>
                  <p>{cat.description || 'Select this category'}</p>
                  {typeof cat.creationGcoinPrice === 'number' && (
                    <p style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#aaa' }}>
                      {t('createevent.creation_fee', 'Creation fee')}: <strong>{cat.creationGcoinPrice}</strong> G-Coins
                    </p>
                  )}
                  <p style={{ marginTop: '0.35rem', fontSize: '0.85rem', color: '#aaa' }}>
                    {t('createevent.max_duration', 'Max duration')}: <strong>{maxDurationLabel}</strong>
                  </p>
                </CategoryCard>
              );
            })()
          ))}
        </div>
        <Button type="submit" disabled={loading || !eventData.categoryId}>
          {loading ? t('common.loading', 'Loading...') : t('createevent.next_basic_info', 'Next: Basic Info')}
        </Button>
      </form>
    );
  };

  const renderStep2 = () => {
    const setStartDateNow = () => {
      const now = new Date();
      // Format to datetime-local input format: YYYY-MM-DDTHH:MM
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}T${hours}:${minutes}`;
      setBasicInfo(prev => ({ ...prev, startDate: formattedDate }));
      if (basicStartDateRef.current) basicStartDateRef.current.value = formattedDate;

      const selectedCategory = categories.find(c => String(c.id) === String(eventData.categoryId));
      const maxMinutes = selectedCategory?.maxDurationMinutes ?? null;
      const hoursVal = durationHoursRef.current?.value ?? basicInfo.durationHours;
      const computed = computeEndDateFromStartAndHours({ startDate: formattedDate, durationHours: hoursVal, maxDurationMinutes: maxMinutes });
      if (computed) {
        if (basicEndDateRef.current) basicEndDateRef.current.value = computed.endDate;
        if (durationHoursRef.current) durationHoursRef.current.value = String(computed.durationHours);
        setBasicInfo(prev => ({ ...prev, endDate: computed.endDate, durationHours: computed.durationHours }));
      }
    };

    return (
      <form onSubmit={(e) => { e.preventDefault(); }}>
        <h4>{t('createevent.step2_basic_info', 'Step 2: Basic Info')}</h4>
        <FieldGroup label={t('createevent.event_name', 'Event Name')}>
          <InputGroup
            ref={basicNameRef}
            name="name"
            defaultValue={basicInfo.name}
            placeholder="My Awesome Event"
            autoComplete="off"
            required
          />
        </FieldGroup>
        <FieldGroup label={t('event.description', 'Description')}>
          <InputGroup
            ref={basicDescriptionRef}
            name="description"
            defaultValue={basicInfo.description}
            placeholder="Details..."
            autoComplete="off"
            required
          />
        </FieldGroup>
        <FieldGroup label={t('event.startDate', 'Start date')}>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <InputGroup
              ref={basicStartDateRef}
              name="startDate"
              type="datetime-local"
              defaultValue={basicInfo.startDate}
              onChange={() => {
                const startDate = basicStartDateRef.current?.value || '';
                setBasicInfo(prev => ({ ...prev, startDate }));
                const selectedCategory = categories.find(c => String(c.id) === String(eventData.categoryId));
                const maxMinutes = selectedCategory?.maxDurationMinutes ?? null;
                const hoursVal = durationHoursRef.current?.value ?? basicInfo.durationHours;
                const computed = computeEndDateFromStartAndHours({ startDate, durationHours: hoursVal, maxDurationMinutes: maxMinutes });
                if (computed) {
                  if (basicEndDateRef.current) basicEndDateRef.current.value = computed.endDate;
                  if (durationHoursRef.current) durationHoursRef.current.value = String(computed.durationHours);
                  setBasicInfo(prev => ({ ...prev, endDate: computed.endDate, durationHours: computed.durationHours }));
                }
              }}
              required
              style={{ flex: 1 }}
            />
            <Button
              type="button"
              onClick={setStartDateNow}
              style={{ background: '#10B981', padding: '10px 20px', whiteSpace: 'nowrap' }}
            >
              ⏰ Now
            </Button>
          </div>
        </FieldGroup>
        <FieldGroup label={t('event.duration', 'Duration (hours)')}>
          <InputGroup
            ref={durationHoursRef}
            name="durationHours"
            type="number"
            min="0"
            step="0.5"
            max={(() => {
              const selectedCategory = categories.find(c => String(c.id) === String(eventData.categoryId));
              const maxMinutes = selectedCategory?.maxDurationMinutes ?? null;
              if (maxMinutes === null || maxMinutes === undefined) return undefined;
              const maxH = Number(maxMinutes) / 60;
              return Number.isFinite(maxH) ? maxH : undefined;
            })()}
            defaultValue={basicInfo.durationHours ?? 1}
            onFocus={selectAllIfZero}
            onChange={() => {
              const selectedCategory = categories.find(c => String(c.id) === String(eventData.categoryId));
              const maxMinutes = selectedCategory?.maxDurationMinutes ?? null;
              const startDate = basicStartDateRef.current?.value ?? basicInfo.startDate;
              const hoursVal = durationHoursRef.current?.value ?? basicInfo.durationHours;
              if (!startDate) {
                setBasicInfo(prev => ({ ...prev, durationHours: Number(hoursVal) }));
                return;
              }
              const computed = computeEndDateFromStartAndHours({ startDate, durationHours: hoursVal, maxDurationMinutes: maxMinutes });
              if (computed) {
                if (basicEndDateRef.current) basicEndDateRef.current.value = computed.endDate;
                if (durationHoursRef.current) durationHoursRef.current.value = String(computed.durationHours);
                setBasicInfo(prev => ({ ...prev, endDate: computed.endDate, durationHours: computed.durationHours }));
                if (computed.wasCapped) {
                  toast.info(t('createevent.duration_capped', 'Duration capped to the max allowed.'));
                }
              }
            }}
            placeholder="e.g. 2"
          />
          {(() => {
            const selectedCategory = categories.find(c => String(c.id) === String(eventData.categoryId));
            const maxMinutes = selectedCategory?.maxDurationMinutes ?? null;
            if (maxMinutes === null || maxMinutes === undefined) return null;
            const maxH = Number(maxMinutes) / 60;
            if (!Number.isFinite(maxH)) return null;
            return (
              <p style={{ color: '#888', fontSize: '0.85rem', marginTop: '8px' }}>
                {t('createevent.max_duration_for', 'Max duration for')} {selectedCategory?.name || t('createevent.this_category', 'this category')}: <strong>{maxH}</strong> {t('createevent.hours', 'hours')}
              </p>
            );
          })()}
        </FieldGroup>

        <FieldGroup label={t('createevent.end_date_auto', 'End date (auto)')}>
          <div style={{
            padding: '10px 12px',
            borderRadius: '6px',
            background: '#00000014',
            border: '1px solid #00000022',
            color: '#666'
          }}>
            {basicInfo.endDate ? basicInfo.endDate.replace('T', ' ') : '—'}
          </div>
          <input ref={basicEndDateRef} type="hidden" name="endDate" defaultValue={basicInfo.endDate} />
        </FieldGroup>
        <Button type="button" onClick={handleStep2Submit} disabled={loading}>
          {loading ? t('createevent.creating', 'Creating...') : t('createevent.next_configuration', 'Next: Configuration')}
        </Button>
      </form>
    );
  };

  const renderStep3 = () => {
    // Get selected category to determine available options
    const selectedCategory = categories.find(cat => cat.id === eventData.categoryId);
    const isTournament = selectedCategory?.slug === 'tournament';
    const isStream = selectedCategory?.slug === 'stream';
    const isWorkshop = selectedCategory?.slug === 'workshop';

    // Define ticket types based on category
    const getTicketTypes = () => {
      if (isTournament) {
        return [
          { value: 'full', label: t('createevent.ticket_type_full_tournament_pass', 'Full Tournament Pass') }
        ];
      } else {
        // Stream and Workshop both use single ticket
        return [
          { value: 'single', label: t('createevent.ticket_type_single_ticket', 'Single Ticket') }
        ];
      }
    };

    return (
      <form onSubmit={handleStep3Submit}>
        <h4>{t('createevent.step3_configuration', 'Step 3: Configuration')}</h4>

        {/* Game Selection - For Stream and Tournament only */}
        {(isTournament || isStream) && (
          <>
            <SectionTitle>
              {isTournament
                ? t('createevent.tournament_details', 'Tournament Details')
                : t('createevent.stream_details', 'Stream Details')}
            </SectionTitle>
            <FieldGroup label={t('createevent.game', 'Game')}>
              {eventData.gameId !== 'random' ? (
                <SelectGames
                  placeholder={t('createevent.search_game_or_random', 'Search for a game or select Random')}
                  id="game-select"
                  data={eventData}
                  setdata={(gameData) => {
                    if (gameData === 'random') {
                      setEventData(prev => ({ ...prev, gameId: 'random', gameName: 'Random' }));
                    } else {
                      setEventData(prev => ({ ...prev, gameId: gameData.gameId, gameName: gameData.gameName }));
                    }
                  }}
                />
              ) : (
                <div style={{
                  padding: '10px',
                  background: '#2a2a2a',
                  border: '1px solid #444',
                  borderRadius: '8px',
                  color: '#888'
                }}>
                  🎲 {t('createevent.random_game_selected', 'Random Game Selected')} - {t('createevent.game_input_disabled', 'Game input disabled')}
                </div>
              )}
              {/* Random option button */}
              <div style={{ marginTop: '10px', display: 'flex', gap: '10px', alignItems: 'center' }}>
                <Button
                  type="button"
                  onClick={() => setEventData(prev => ({ ...prev, gameId: 'random', gameName: 'Random' }))}
                  style={{ background: '#666', padding: '8px 16px', fontSize: '0.9rem' }}
                >
                  🎲 {t('createevent.random_game', 'Random Game')}
                </Button>
                {eventData.gameId === 'random' && (
                  <Button
                    type="button"
                    onClick={() => setEventData(prev => ({ ...prev, gameId: '', gameName: '' }))}
                    style={{ background: '#EF4444', padding: '8px 16px', fontSize: '0.9rem' }}
                  >
                    ✕ {t('createevent.clear_random', 'Clear Random')}
                  </Button>
                )}
                {eventData.gameName && eventData.gameId !== 'random' && (
                  <span style={{ color: '#10B981' }}>
                    {t('createevent.selected', 'Selected')}: {eventData.gameName}
                  </span>
                )}
              </div>
            </FieldGroup>
            <FieldGroup label={t('createevent.tags', 'Tags')}>
              <TagsSelect
                tags={eventData.tags}
                settags={(newTags) => setEventData(prev => ({ ...prev, tags: newTags }))}
                maxTags={10}
                placeholder={t('createevent.add_tags_placeholder', 'Add tags (press Enter)')}
              />
            </FieldGroup>
          </>
        )}

        {/* Streaming Options - All categories have restream and replay */}
        <SectionTitle>{t('createevent.streaming_options', 'Streaming Options')}</SectionTitle>
        <Row>
          <Checkbox
            name="options.restream"
            checked={!!eventData.options.restream}
            onChange={handleChange}
          >
            {t('createevent.enable_restream', 'Enable Restream')}
          </Checkbox>
          <Checkbox
            name="options.autoReplay"
            checked={!!eventData.options.autoReplay}
            onChange={handleChange}
          >
            {t('createevent.auto_generate_replay', 'Auto-Generate Replay')}
          </Checkbox>
        </Row>
        <p style={{ color: '#888', fontSize: '0.85rem', marginTop: '-10px', marginBottom: '15px', marginLeft: '5px' }}>
          {t('createevent.restream_hint', 'Restream will use platforms configured in Settings → Profile (Facebook, Twitch, YouTube)')}
        </p>

        {/* Multi-POV - Tournament only */}
        {isTournament && (
          <Row>
            <Checkbox
              name="options.multiPov"
              checked={!!eventData.options.multiPov}
              onChange={handleChange}
            >
              {t('createevent.multi_pov', 'Multi-POV (Multiple Streamers / Angles)')}
            </Checkbox>
          </Row>
        )}

        {/* Auto-Highlights - disabled for now */}

        {/* Chat Mode for Stream only */}
        {isStream && (
          <Row>
            <FieldGroup label={t('createevent.chat_mode', 'Chat Mode')}>
              <ThemedSelect
                name="options.chatMode"
                value={eventData.options.chatMode ?? 'public'}
                onChange={handleChange}
              >
                <option value="public">{t('createevent.chat_mode_public', 'Public')}</option>
                <option value="subscribers">{t('createevent.chat_mode_subscribers', 'Subscribers Only')}</option>
                <option value="disabled">{t('createevent.chat_mode_disabled', 'Disabled')}</option>
              </ThemedSelect>
            </FieldGroup>
          </Row>
        )}

        {/* Tournament-specific inputs */}
        {isTournament && (
          <>
            <SectionTitle>{t('createevent.tournament_settings', 'Tournament Settings')}</SectionTitle>
            <Row>
              <FieldGroup label={t('createevent.team_size_label', 'Team Size (0 for solo)')}>
                <InputGroup
                  ref={teamSizeRef}
                  name="options.teamSize"
                  type="number"
                  onChange={handleChange}
                  onFocus={selectAllIfZero}
                  defaultValue={eventData.options.teamSize === '' ? '' : (eventData.options.teamSize ?? 0)}
                  placeholder={t('createevent.team_size_placeholder', '0 = solo, 2 = duo, etc.')}
                />
              </FieldGroup>
            </Row>
            <Row>
              <FieldGroup label={t('createevent.prize_pool_label', 'Prize Pool (G-Coins)')}>
                <InputGroup
                  ref={prizePoolRef}
                  name="options.prizePool"
                  type="number"
                  onChange={handleChange}
                  onFocus={selectAllIfZero}
                  defaultValue={eventData.options.prizePool === '' ? '' : (eventData.options.prizePool ?? 0)}
                  placeholder={t('createevent.prize_pool_placeholder', 'Total prize pool')}
                />
              </FieldGroup>
              <FieldGroup label={t('createevent.tournament_format', 'Tournament Format')}>
                <ThemedSelect
                  name="options.tournamentFormat"
                  value={eventData.options.tournamentFormat ?? 'single-elimination'}
                  onChange={handleChange}
                >
                  <option value="single-elimination">{t('createevent.format_single_elimination', 'Single Elimination')}</option>
                  <option value="double-elimination">{t('createevent.format_double_elimination', 'Double Elimination')}</option>
                  <option value="round-robin">{t('createevent.format_round_robin', 'Round Robin')}</option>
                  <option value="swiss">{t('createevent.format_swiss', 'Swiss')}</option>
                </ThemedSelect>
              </FieldGroup>
            </Row>
          </>
        )}

        {/* Workshop-specific inputs */}
        {isWorkshop && (
          <>
            <SectionTitle>{t('createevent.workshop_settings', 'Workshop Settings')}</SectionTitle>
            <FieldGroup label={t('createevent.tags', 'Tags')}>
              <TagsSelect
                tags={eventData.tags}
                settags={(newTags) => setEventData(prev => ({ ...prev, tags: newTags }))}
                maxTags={10}
                placeholder={t('createevent.add_tags_placeholder', 'Add tags (press Enter)')}
              />
            </FieldGroup>
            <Row>
              <FieldGroup label={t('createevent.max_participants_label', 'Max Participants (0 = unlimited)')}>
                <InputGroup
                  ref={maxParticipantsRef}
                  name="options.maxParticipants"
                  type="number"
                  onChange={handleChange}
                  onFocus={selectAllIfZero}
                  defaultValue={eventData.options.maxParticipants === '' ? '' : (eventData.options.maxParticipants ?? 0)}
                  placeholder={t('createevent.max_participants_placeholder', 'e.g., 30')}
                />
              </FieldGroup>
            </Row>
          </>
        )}

        {/* Ticketing - Common for all */}
        <SectionTitle>{t('createevent.ticketing', 'Ticketing')}</SectionTitle>
        <Row>
          <FieldGroup label={t('createevent.ticket_type', 'Ticket Type')}>
            <ThemedSelect
              name="ticket.type"
              value={eventData.ticketConfig.type}
              onChange={handleChange}
            >
              {getTicketTypes().map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </ThemedSelect>
          </FieldGroup>
          <FieldGroup label={t('createevent.ticket_price_label', 'Price (G-Coins, 0 = Free)')}>
            <InputGroup
              ref={ticketPriceRef}
              name="ticket.price"
              type="number"
              min="0"
              onChange={handleChange}
              onFocus={selectAllIfZero}
              defaultValue={eventData.ticketConfig.price === '' ? '' : (eventData.ticketConfig.price ?? 0)}
              placeholder={t('createevent.ticket_price_placeholder', '0 for free/public event')}
            />
          </FieldGroup>
        </Row>

        <Button type="submit" disabled={loading}>
          {isTournament
            ? t('createevent.next_manage_participants', 'Next: Manage Participants')
            : isStream
              ? t('createevent.next_go_live', 'Next: Go Live')
              : t('createevent.next_dashboard', 'Next: Dashboard')}
        </Button>
      </form>
    );
  };

  const renderStep4 = () => {
    // Check if this is a tournament
    const selectedCategory = categories.find(cat => cat.id === eventData.categoryId);
    const isTournament = selectedCategory?.slug === 'tournament';

    if (isTournament) {
      // Tournament: Participant Management
      return (
        <div>
          <h4>{t('createevent.step4_manage_tournament_participants', 'Step 4: Manage Tournament Participants')}</h4>
          <SectionTitle>{t('createevent.invite_streamers', 'Invite Streamers')}</SectionTitle>
          <p style={{ color: '#888', marginBottom: '1rem' }}>
            {t('createevent.invite_streamers_hint', 'Invite streamers to participate and broadcast their POV (Multi-POV feature).')}
          </p>

          {/* Streamer Invitation */}
          <StreamerAssignmentManager
            eventSlug={createdEventSlug}
            onAssign={(streamer) => {
              toast.success(t('createevent.invited_user', 'Invited') + ` ${streamer.username}`);
              setInvitedStreamers(prev => [...prev, streamer]);
            }}
          />

          {invitedStreamers.length > 0 && (
            <div style={{ marginTop: '1.5rem' }}>
              <SectionTitle>{t('createevent.invited_players_streamers', 'Invited Players/Streamers')}</SectionTitle>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {invitedStreamers.map((s, idx) => (
                  <li key={idx} style={{ padding: '6px 0', color: '#ddd', borderBottom: '1px solid #333' }}>
                    🎮 {s.username}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div style={{ marginTop: '2rem', borderTop: '1px solid #333', paddingTop: '1rem' }}>
            <Button onClick={handleFinish} disabled={loading} variant="primary">
              {loading ? t('createevent.publishing', 'Publishing...') : `🚀 ${t('createevent.publish_tournament', 'Publish Tournament')}`}
            </Button>
          </div>
        </div>
      );
    } else {
      // Stream/Workshop: Should not reach here, but fallback
      return (
        <div>
          <h4>{t('createevent.step4_finish', 'Step 4: Finish')}</h4>
          <Button onClick={handleFinish} disabled={loading} variant="primary">
            {loading ? t('createevent.publishing', 'Publishing...') : `🚀 ${t('createevent.publish_event', 'Publish Event')}`}
          </Button>
        </div>
      );
    }
  };

  if (!isLoggedIn) {
    return (
      <CreateEventContainer>
        <LeftColumn>
          <CreateEventStyle>
            <h4>{t('createevent.login_required_title', 'Connexion requise')}</h4>
            <p style={{ color: "#888" }}>
              {t('createevent.login_required_message', 'Veuillez vous connecter pour créer un événement.')}
            </p>
            <Button type="button" variant="primary" onClick={openLoginModal}>
              {t('auth.login', 'Se connecter')}
            </Button>
          </CreateEventStyle>
        </LeftColumn>
      </CreateEventContainer>
    );
  }

  return (
    <CreateEventContainer>
      <LeftColumn>
        <CreateEventStyle>
          {/* Progress Bar could go here */}
          <div style={{ marginBottom: '1rem', color: '#888' }}>
            Step {step} of 4
          </div>

          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
          {step === 4 && renderStep4()}

        </CreateEventStyle>
      </LeftColumn>

      <RightColumn>
        <WalletWidget userId={user?.id} />
        <EventCostEstimator
          eventData={eventData}
          categories={categories}
          onCostUpdate={(amount, canAfford) => {
            setCreationFeeState(prev => {
              if (prev.amount === amount && prev.canAfford === canAfford) return prev;
              return { amount, canAfford };
            });
          }}
        />
      </RightColumn>
    </CreateEventContainer>
  );
};

// Styled Components
const CreateEventContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 350px;
  gap: 2rem;
  max-width: 1200px;
  margin: 2rem auto;
  padding: 0 1rem;
  
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const LeftColumn = styled.div`
  min-width: 0;
`;

const RightColumn = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const CreateEventStyle = styled.div`
  background: ${({ theme }) => theme.color.background};
  border: 1px solid ${({ theme }) => theme.color.border};
  padding: 2rem;
  border-radius: 8px;
  
  h4 {
    margin-bottom: 1.5rem;
    color: ${({ theme }) => theme.color.darkerText};
    font-size: 1.5rem;
    border-bottom: 1px solid ${({ theme }) => theme.color.border};
    padding-bottom: 1rem;
  }
  
  form {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }
`;

const CategoryCard = styled.div`
  background: ${({ selected, theme }) => {
    if (selected) return theme.mode === 'dark' ? `${theme.color.primary}33` : `${theme.color.primary}14`;
    return theme.color.secondaryBg;
  }};
  border: 2px solid ${({ selected, theme }) => (selected ? theme.color.primary : theme.color.border)};
  padding: 1.5rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  text-align: center;
  color: ${({ theme }) => theme.color.darkerText};

  strong {
    display: block;
    margin-top: 0.25rem;
    color: inherit;
  }

  p {
    margin-top: 0.5rem;
    color: ${({ theme }) => theme.color.lighterText};
  }

  &:hover {
    filter: ${({ theme }) => (theme.mode === 'dark' ? 'brightness(1.06)' : 'brightness(0.99)')};
  }
`;

const SectionTitle = styled.h5`
    color: ${({ theme }) => theme.color.lighterText};
    text-transform: uppercase;
    font-size: 0.8rem;
    letter-spacing: 1px;
    margin-bottom: 0.5rem;
    margin-top: 1rem;
`;

export default CreateEvent;

